import { differenceInMonths, subMonths } from 'date-fns';
import {
  ICompanyFinancialsDataModel,
  isKpiPeriod,
  KpiPeriod,
} from '../../../../data-models/company-financials.data-model';
import { monthsInPeriod } from '../../Performance/components/DateSettings/DateSettings';
import { endOfFiscalPeriod } from '../../utils/financialUtils';

export const MAX_RANGE = -1;

export const PastPeriodRangeOptions: Record<KpiPeriod, number[]> = {
  [KpiPeriod.month]: [6, 12, 24],
  [KpiPeriod.quarter]: [4, 8, 12],
  [KpiPeriod.year]: [2, 3, 5],
};
export const FuturePeriodRangeOptions: Record<KpiPeriod, number[]> = {
  [KpiPeriod.month]: [12, 24],
  [KpiPeriod.quarter]: [4, 8],
  [KpiPeriod.year]: [2],
};
export function periodRangeToLabel(frequency: KpiPeriod, value: number, future: boolean = false) {
  if (value === MAX_RANGE) {
    return 'Max';
  }
  return `${future ? 'Next' : 'Last'} ${value} ${frequency}s`;
}

export function rangeOptions(frequency: KpiPeriod, future: boolean = false) {
  const options = future ? FuturePeriodRangeOptions : PastPeriodRangeOptions;
  return Array.from(options[frequency]).map((value) => {
    return {
      value,
      displayName: periodRangeToLabel(frequency, value, future),
    };
  });
}

export function defaultRangeForFrequency(frequency: KpiPeriod, future: boolean = false) {
  if (!future) {
    return MAX_RANGE;
  }

  const options = FuturePeriodRangeOptions;
  const range = options[frequency];

  if (!range || range.length === 0) {
    console.error(`Invalid frequency "${frequency}" - expected one of: ${Object.keys(options).join(', ')}`);
    return options[KpiPeriod.quarter][0];
  }

  return range[0];
}

/** @param fye [0-11]  */
export function endOfPreviousFiscalPeriod(fye: number, frequency: KpiPeriod, currentDate: Date = new Date()) {
  const prevPeriodDate = subMonths(currentDate, monthsInPeriod[frequency]);
  return endOfFiscalPeriod(prevPeriodDate, frequency, fye);
}

export function isMaxMode(pastRange: number | null, futureRange: number | null): boolean {
  return pastRange === MAX_RANGE && futureRange === MAX_RANGE;
}

export function financialsDateRangeLabel(
  frequency: KpiPeriod,
  pastRange: number | null,
  futureRange: number | null
) {
  if (isMaxMode(pastRange, futureRange)) {
    return 'Max';
  }
  const pastLabel = pastRange != null ? periodRangeToLabel(frequency, pastRange) : '';
  const futureLabel = futureRange ? periodRangeToLabel(frequency, futureRange, true) : '';
  const separator = pastLabel && futureLabel ? ' / ' : '';
  return `${pastLabel}${separator}${futureLabel}`;
}

/**
 * Compute the number of past periods needed to cover all financial data at the
 * requested frequency, measured from `endDate` backward to the earliest
 * available data point.
 *
 * Earliest-available is computed across **any** standard KpiPeriod
 * (Month/Quarter/Year), not only entries that match `frequency`. Many tenants
 * store data at finer granularity (e.g., monthly entries only) and rely on
 * client-side aggregation for yearly/quarterly views — filtering to exact
 * period match made "Max" collapse to the fallback or even to 1 column.
 */
export function computeMaxPastPeriods(
  financials: ICompanyFinancialsDataModel[],
  frequency: KpiPeriod,
  endDate: Date
): number {
  const relevantDates = financials
    .filter((f) => isKpiPeriod(f.period as string) && new Date(f.date) <= endDate)
    .map((f) => new Date(f.date));

  if (relevantDates.length === 0) {
    const fallbackOptions = PastPeriodRangeOptions[frequency];
    return fallbackOptions[0];
  }

  const earliestDate = relevantDates.reduce((a, b) => (a < b ? a : b));
  const diffMonths = differenceInMonths(endDate, earliestDate);
  const periodsPerMonth = monthsInPeriod[frequency];
  return Math.max(1, Math.ceil(diffMonths / periodsPerMonth) + 1);
}

/**
 * Compute the number of future periods at the requested frequency that have
 * budget/forecast data beyond `endDate`.
 *
 * Like `computeMaxPastPeriods`, this looks at any standard KpiPeriod entry
 * (not just `frequency`) so future ranges aren't artificially capped when
 * future data is stored at a different granularity than the user picked.
 */
export function computeMaxFuturePeriods(
  financials: ICompanyFinancialsDataModel[],
  frequency: KpiPeriod,
  endDate: Date
): number {
  const futureDates = financials
    .filter((f) => isKpiPeriod(f.period as string) && new Date(f.date) > endDate)
    .map((f) => new Date(f.date));

  if (futureDates.length === 0) {
    return 0;
  }

  const latestDate = futureDates.reduce((a, b) => (a > b ? a : b));
  const diffMonths = differenceInMonths(latestDate, endDate);
  const periodsPerMonth = monthsInPeriod[frequency];
  return Math.max(1, Math.ceil(diffMonths / periodsPerMonth));
}
